import * as React from "react"
import { Loader2 } from "lucide-react"
import { Button, type ButtonProps } from "@/components/ui/button"
import { cn } from "@/lib/utils"

// ─── MUI-style color × variant class map ───────────────────────────────────
const colorMap: Record<string, Record<string, string>> = {
  contained: {
    primary:   "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-xl",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-md hover:shadow-xl",
    error:     "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md hover:shadow-xl",
    success:   "bg-success text-success-foreground hover:bg-success/90 shadow-md hover:shadow-xl",
    warning:   "bg-warning text-warning-foreground hover:bg-warning/90 shadow-md hover:shadow-xl",
    info:      "bg-info text-info-foreground hover:bg-info/90 shadow-md hover:shadow-xl",
  },
  outlined: {
    primary:   "border border-primary text-primary bg-transparent hover:bg-primary/10 hover:shadow-lg",
    secondary: "border border-secondary text-secondary-foreground bg-transparent hover:bg-secondary/20 hover:shadow-lg",
    error:     "border border-destructive text-destructive bg-transparent hover:bg-destructive/10 hover:shadow-lg",
    success:   "border border-success text-success bg-transparent hover:bg-success/10 hover:shadow-lg",
    warning:   "border border-warning text-warning bg-transparent hover:bg-warning/10 hover:shadow-lg",
    info:      "border border-info text-info bg-transparent hover:bg-info/10 hover:shadow-lg",
  },
  text: {
    primary:   "font-semibold text-primary bg-transparent hover:bg-primary/10 shadow-none hover:shadow-none",
    secondary: "font-semibold text-secondary-foreground bg-transparent hover:bg-secondary/20 shadow-none hover:shadow-none",
    error:     "font-semibold text-destructive bg-transparent hover:bg-destructive/10 shadow-none hover:shadow-none",
    success:   "font-semibold text-success bg-transparent hover:bg-success/10 shadow-none hover:shadow-none",
    warning:   "font-semibold text-warning bg-transparent hover:bg-warning/10 shadow-none hover:shadow-none",
    info:      "font-semibold text-info bg-transparent hover:bg-info/10 shadow-none hover:shadow-none",
  },
}

// ─── Size map → shadcn size ─────────────────────────────────────────────────
const sizeMap: Record<string, ButtonProps["size"]> = {
  sm:   "sm",
  md:   "md",
  lg:   "lg",
  icon: "icon",
}

// ─── Size → border-radius override ─────────────────────────────────────────
const sizeRadiusMap: Record<string, string> = {
}

// ─── Props ──────────────────────────────────────────────────────────────────
export interface AppButtonProps extends Omit<ButtonProps, "variant" | "size"> {
  /** MUI-style visual variant */
  variant?:   "contained" | "outlined" | "text"
  /** Semantic color token */
  color?:     "primary" | "secondary" | "error" | "success" | "warning" | "info"
  /** Button size */
  size?:      "sm" | "md" | "lg" | "xl" | "icon"
  /** Stretch to full container width */
  fullWidth?: boolean
  /** Shows spinner and disables the button */
  loading?:   boolean
  /** Icon rendered before the label */
  startIcon?: React.ReactNode
  /** Icon rendered after the label */
  endIcon?:   React.ReactNode
}

// ─── Component ──────────────────────────────────────────────────────────────
const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      className,
      variant  = "contained",
      color    = "primary",
      size     = "md",
      fullWidth = false,
      loading  = false,
      startIcon,
      endIcon,
      disabled,
      children,
      asChild,
      ...props
    },
    ref
  ) => {
    const colorClass = colorMap[variant]?.[color] ?? ""

    return (
      <Button
        ref={ref}
        asChild={asChild}
        // pass "default" so shadcn doesn't add its own color classes
        variant="default"
        size={sizeMap[size]}
        disabled={disabled || loading}
        className={cn(
          "cursor-pointer rounded-full transition-all duration-300 ease-in-out hover:-translate-y-1 active:translate-y-0",
          colorClass,
          sizeRadiusMap[size],
          fullWidth && "w-full",
          className
        )}
        {...props}
      >
        {/* When asChild is true, Slot requires a single child — skip decorators */}
        {asChild ? children : (
          <>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            ) : (
              startIcon && <span className="shrink-0 inline-flex">{startIcon}</span>
            )}
            {children}
            {!loading && endIcon && (
              <span className="shrink-0 inline-flex">{endIcon}</span>
            )}
          </>
        )}
      </Button>
    )
  }
)
AppButton.displayName = "AppButton"

export { AppButton }
